import { useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink, FileText, Globe, Video, Folder, BookOpen, Github } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { SourceContext } from "@/services/contextOrchestrator";
import { asString } from "@/lib/chatHistoryLimits";
import { getSnippet, getSourceTypeIcon } from "@/lib/citationUtils";

interface ContextCitationsFooterProps {
  sources: SourceContext[];
  /** When provided, clicking a document source opens it in the right sidebar document creator */
  onOpenDocument?: (documentId: string) => void;
}

/** Get knowledge-base document id from a source if it refers to a document (not a thread). */
function getDocumentIdFromSource(source: SourceContext): string | null {
  const id = asString(source.id);
  if (!id || id.startsWith("rag_thread_")) return null;
  const m = id.match(/^rag_(.+)_\d+$/);
  if (m) return m[1];
  return id;
}

function getIconComponent(iconName: string) {
  switch (iconName) {
    case "globe":
      return Globe;
    case "video":
      return Video;
    case "folder":
      return Folder;
    case "book-open":
      return BookOpen;
    case "github":
      return Github;
    case "file-text":
    default:
      return FileText;
  }
}

export function ContextCitationsFooter({ sources, onOpenDocument }: ContextCitationsFooterProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) {
    return null;
  }

  return (
    <div className="mt-3" data-testid="context-citations-container">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-muted-foreground"
        data-testid="button-toggle-context-citations"
      >
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
        <span className="text-sm">
          {sources.length} knowledge {sources.length === 1 ? "source" : "sources"} referenced
        </span>
      </Button>

      {isExpanded && (
        <div className="mt-2 space-y-2" data-testid="context-citations-list">
          {sources.map((source) => {
            const iconName = getSourceTypeIcon(source.type);
            const IconComponent = getIconComponent(iconName);
            const snippet = getSnippet(source.content, 150);
            const documentId = getDocumentIdFromSource(source);
            const isDocument = !!documentId && !!onOpenDocument;

            return (
              <Card
                key={source.id}
                className={`p-3 hover-elevate ${isDocument ? "cursor-pointer" : ""}`}
                data-testid={`context-citation-${source.citationIndex}`}
                role={isDocument ? "button" : undefined}
                onClick={
                  isDocument
                    ? (e) => {
                        if ((e.target as HTMLElement).closest("a")) return;
                        onOpenDocument(documentId);
                      }
                    : undefined
                }
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                      {source.citationIndex}
                    </span>
                    <div className="p-1.5 rounded-md bg-muted">
                      <IconComponent className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">
                      {source.title || "Untitled Source"}
                    </h4>

                    {(source.spaceName || source.topicName) && (
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {source.spaceName && (
                          <span
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                            data-testid={`context-citation-space-${source.citationIndex}`}
                          >
                            Space: {source.spaceName}
                          </span>
                        )}
                        {source.topicName && (
                          <span
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                            data-testid={`context-citation-topic-${source.citationIndex}`}
                          >
                            Topic: {source.topicName}
                          </span>
                        )}
                      </div>
                    )}

                    {snippet && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {snippet}
                      </p>
                    )}

                    {source.url && (
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
                        data-testid={`context-citation-link-${source.citationIndex}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="truncate max-w-[300px]">
                          {source.url}
                        </span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
